import { getDefaultIntegrations, init, captureMessage, type ErrorEvent } from '@sentry/browser';

import { APP_VERSION } from './appVersion';
import { desktopBruecke } from './gleis';

/**
 * Fehlerberichte an GlitchTip (Auftrag des Nutzers, 2026-10-19).
 *
 * Drei Projekte, drei DSN: dieses Modul ist der **Webteil** (WKWebView am iPad, Fenster am
 * Desktop, Browser). Die native Seite meldet selbst — Swift in `AppDelegate.swift`, der
 * Electron-Hauptprozess in `desktop/fehlerberichte.cts`. Beide fragen, ob sie dürfen; die
 * Antwort reicht `meldeErlaubnis` hinüber, weil nur hier die Rangfolge der Einstellungen gilt.
 *
 * ⛔ **Darf den Ablauf nie stören.** Alles läuft in `try`, nichts wartet auf das Netz; ohne
 * Verbindung verwirft das SDK den Bericht still.
 *
 * Gesendet wird nur, solange `error_reporting` an ist **und** keine Simulationsprüfung
 * ausgewählt ist (Nutzer, 2026-10-19: Simulation sendet nichts). Fehlertexte gehen ungekürzt
 * hinaus — wer das nicht will, schaltet ab.
 */

const DSN = 'https://0fcc88b1cf5c47369fd8a059f12ca499@glitchtip.fairexa.org/9';

/**
 * Was vom SDK bleibt. ⛔ Nicht `Console`: die Konsole trägt das SQL-Log im Klartext, samt
 * Passwörtern und PINs. Nicht `Breadcrumbs`, `BrowserApiErrors`, `FunctionToString`: sie
 * umwickeln `fetch`, Zeitgeber, Horcher und `Function.prototype` — fremde Hände im Ablauf.
 * Nicht `BrowserSession`: ein Bericht je Start, auch ohne Fehler.
 */
const BEHALTEN = new Set(['EventFilters', 'GlobalHandlers', 'LinkedErrors', 'Dedupe', 'HttpContext', 'CultureContext']);

/** Unbekannt, bis die Einstellungen gemischt sind — dann gilt, was zuletzt galt. */
let erlaubt: boolean | null = null;
const MERKER = 'fairexa.fehlerberichte';

function zuletztErlaubt(): boolean {
  try {
    return globalThis.localStorage?.getItem(MERKER) !== '0';
  } catch {
    return true;
  }
}

export function darfSenden(): boolean {
  return erlaubt ?? zuletztErlaubt();
}

export function sperreOderGib<T>(ereignis: T): T | null {
  return darfSenden() ? ereignis : null;
}

let gestartet = false;

/** Ganz vorn in `main.tsx`. */
export function starteFehlerberichte(): void {
  try {
    init({
      dsn: DSN,
      release: APP_VERSION,
      environment: import.meta.env.DEV ? 'dev' : 'release',
      // ⛔ Sonst schreibt das SDK den Text echter `fetch`-Fehler um — und der Code liest ihn.
      enhanceFetchErrorMessages: false,
      defaultIntegrations: false,
      integrations: getDefaultIntegrations({}).filter((i) => BEHALTEN.has(i.name)),
      beforeSend: (ereignis: ErrorEvent) => sperreOderGib(ereignis),
    });
    gestartet = true;
  } catch {
    // Ohne Berichte weiter — nie ohne App.
  }
}

/**
 * Eine Logzeile der Stufe `error` als Bericht (`appLog.ts`).
 *
 * Ausser den Zeilen von `absturz.ts`: dieselben Fehler fängt das SDK selbst, mit Stapel.
 */
export function meldeFehlerzeile(text: string): void {
  if (!gestartet || text.startsWith('Ungefangene ')) return;
  try {
    captureMessage(text, 'error');
  } catch {
    // Ein Bericht, der wirft, hilft niemandem.
  }
}

/**
 * Die gültige Erlaubnis setzen und dem Hauptprozess am Desktop sagen — ohne zu warten, ohne zu
 * werfen. Gibt zurück, ob sie sich geändert hat; dann sagt `main.tsx` es auch Swift.
 */
export function meldeErlaubnis(an: boolean): boolean {
  if (erlaubt === an) return false;
  erlaubt = an;
  try {
    globalThis.localStorage?.setItem(MERKER, an ? '1' : '0');
  } catch {
    // Privater Modus o. ä. — dann gilt beim nächsten Start bis zum Mischen die Vorgabe.
  }
  void desktopBruecke()?.fehlerberichte(an, APP_VERSION).catch(() => undefined);
  return true;
}
